import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";

/**
 * Portal Bienvenida — full-screen "welcome card" designed to be shown on the TV
 * inside the property when the guest arrives. Displays a large QR pointing to
 * the private /portal/:token, over a cinematic sunset image.
 *
 * Route: /portal/:token/bienvenida
 */
export default function PortalBienvenida() {
  const { token } = useParams();
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/guest-portal/${encodeURIComponent(token)}`);
        if (!cancelled) setPortal(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "No se pudo cargar la bienvenida.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-900 text-stone-300 text-sm tracking-widest uppercase">
        Preparando bienvenida…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-900 text-stone-300 text-sm tracking-widest uppercase p-6 text-center">
        {error}
      </div>
    );
  }

  const guestName = portal?.guest_name || "Invitado";
  const property = portal?.property_label || "";
  const checkIn = portal?.check_in ? new Date(portal.check_in + "T00:00:00").toLocaleDateString("es-MX", { day: "numeric", month: "long" }) : "";
  const checkOut = portal?.check_out ? new Date(portal.check_out + "T00:00:00").toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" }) : "";
  const qrUrl = `${API}/guest-portal/${token}/qr.png`;

  return (
    <div
      data-testid="portal-bienvenida"
      className="min-h-screen w-full flex flex-col items-center justify-center relative overflow-hidden"
      style={{
        backgroundImage: `url('/experiencia/atardecer-palmeras.jpg')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Cinematic overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/70" />
      <div className="absolute inset-0 bg-black/10" />

      {/* Corner marks */}
      <div className="absolute top-8 left-8 text-white/90 text-[10px] tracking-[0.4em] uppercase font-medium">
        Villas Pura Vida
      </div>
      <div className="absolute top-8 right-8 text-white/70 text-[10px] tracking-[0.4em] uppercase">
        Barra de Galindo · Tuxpan · Veracruz
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-8 max-w-4xl">
        <p className="text-white/70 text-[11px] tracking-[0.5em] uppercase mb-6">
          Bienvenidos
        </p>
        <h1
          data-testid="bienvenida-guest-name"
          className="font-serif-display text-white text-6xl md:text-7xl lg:text-8xl leading-[1.05] mb-8"
          style={{ fontFamily: '"Playfair Display", serif' }}
        >
          <span className="italic font-light">{guestName}</span>
        </h1>

        <p className="text-white/90 text-lg md:text-xl italic mb-2 max-w-2xl leading-relaxed font-serif-display">
          Estamos felices de recibirlos.
        </p>
        {(checkIn || checkOut) && (
          <p className="text-white/70 text-sm tracking-[0.25em] uppercase mb-12 mt-2">
            {checkIn && checkOut ? `${checkIn}  —  ${checkOut}` : (checkIn || checkOut)}
            {property && <span className="ml-4">·  {property}</span>}
          </p>
        )}

        {/* QR card */}
        <div className="bg-[#FAFAF9] p-6 md:p-8 shadow-2xl relative">
          <img
            src={qrUrl}
            alt="QR de acceso al Portal"
            className="w-64 h-64 md:w-80 md:h-80 block"
            data-testid="bienvenida-qr-image"
          />
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E07A5F] px-4 py-1">
            <span className="text-white text-[10px] tracking-[0.35em] uppercase font-medium">Escanea con tu celular</span>
          </div>
        </div>

        <div className="mt-8 max-w-xl">
          <p className="text-white/85 text-[13px] leading-relaxed">
            Aquí encontrarás todo sobre su estancia: horarios, amenidades, cómo aprovechar la playa, la fogata,
            el asador y los pequeños secretos de este lugar.
          </p>
          <p className="text-white/60 text-[11px] tracking-[0.3em] uppercase mt-6">
            Que su viaje comience.
          </p>
        </div>
      </div>

      {/* Bottom mark */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-[10px] tracking-[0.5em] uppercase">
        M M X X V I
      </div>
    </div>
  );
}
